import { createStyles, Skeleton, Stack, useMantineTheme } from '@mantine/core';
import CollapsableStack from '@repo/common/components/CollapsableStack';
import { EntityType } from '@repo/common/enums/entityType';
import { Text } from '@repo/foundations';
import { useCallback, useMemo } from 'react';
import { Virtuoso } from 'react-virtuoso';
import type { IQuestion, ISecodaEntity } from '../../../../api';
import { useResourceRelationInfiniteList } from '../../../../api/hooks/relations';
import { type SecodaEntity } from '../../../../lib/models';
import { useCheckEntityUpdatePermission } from '../../../../utils/authorization/roles';
import { SmallLoadingSpinner } from '../../../LoadingSpinner';
import { RelatedAIChat } from './RelatedAIChat';
import { RelatedEntity } from './RelatedEntity';
import { RelatedResourceCreateMenu } from './RelatedResourceCreateMenu';

interface IRelatedResourceSelectorProps {
	entity: SecodaEntity | ISecodaEntity;
	isVisible: boolean;
	withinPortal?: boolean;
}

const useStyles = createStyles((theme) => ({
	virtuosoContainer: {
		'&::-webkit-scrollbar': {
			width: theme.spacing.xs,
			backgroundColor: 'transparent',
		},
		'&::-webkit-scrollbar-track': {
			backgroundColor: 'transparent',
		},
		'&::-webkit-scrollbar-thumb': {
			backgroundColor: theme.other.getColor('fill/tertiary/default'),
			borderRadius: theme.radius.sm,
			border: `2px solid transparent`,
			backgroundClip: 'padding-box',
		},
		'&::-webkit-scrollbar-thumb:hover': {
			backgroundColor: theme.other.getColor('fill/primary/selected'),
		},
		scrollbarColor: `${theme.other.getColor('fill/tertiary/default')} transparent`,
		scrollbarWidth: 'thin',
		scrollbarGutter: 'stable',
		marginLeft: `-${theme.spacing.xs}`,
	},
	virtuosoContainerWithMinHeight: {
		minHeight: 300,
	},
}));

export function RelatedResourceStack({
	isVisible,
	entity,
	withinPortal,
}: IRelatedResourceSelectorProps) {
	const theme = useMantineTheme();
	const { classes } = useStyles();
	const editorOfEntity = useCheckEntityUpdatePermission({
		entity,
	});

	const {
		data: resources,
		isLoading,
		isError,
		hasNextPage,
		isFetchingNextPage,
		fetchNextPage,
	} = useResourceRelationInfiniteList({
		entityId: entity.id,
	});
	const count = resources?.count || 0;

	const data = useMemo(() => resources?.pages || [], [resources?.pages]);
	if (!isVisible || isError) {
		return null;
	}

	const handleEndReached = useCallback(() => {
		if (hasNextPage && !isFetchingNextPage) {
			fetchNextPage?.();
		}
	}, [hasNextPage, isFetchingNextPage, fetchNextPage]);

	const renderFooter = useCallback(() => {
		if (isFetchingNextPage) {
			return <SmallLoadingSpinner />;
		}
		return null;
	}, [isFetchingNextPage]);

	return (
		<CollapsableStack
			groupName="Related"
			collapsedText={String(count > 0 ? count : '')}
			actions={
				editorOfEntity && (
					<RelatedResourceCreateMenu
						entity={entity}
						relatedEntityIds={data?.map((r) => r.id) || []}
					/>
				)
			}
		>
			{isLoading ? (
				<Skeleton h={theme.other.space[10]} />
			) : (
				<Stack spacing="xs">
					{entity.entity_type === EntityType.question ? (
						<>
							{!(entity as IQuestion).ai_prompt && data?.length === 0 && (
								<Text size="sm" color="text/secondary/default">
									{editorOfEntity
										? 'Add related resource'
										: 'No related resource'}
								</Text>
							)}
							<RelatedAIChat
								isEditorOfEntity={editorOfEntity}
								entity={entity as IQuestion}
							/>
						</>
					) : (
						data?.length === 0 && (
							<Text size="sm" color="text/secondary/default">
								{editorOfEntity
									? 'Add related resource'
									: 'No related resource'}
							</Text>
						)
					)}

					<Virtuoso
						data={data}
						itemContent={(_, item) => (
							<RelatedEntity
								key={item.id}
								entityId={entity.id}
								relatedEntity={item}
								isEditorOfEntity={editorOfEntity}
								withinPortal={withinPortal}
							/>
						)}
						endReached={handleEndReached}
						className={`${classes.virtuosoContainer} ${
							data?.length > 0 ? classes.virtuosoContainerWithMinHeight : ''
						}`.trim()}
						components={{
							Footer: renderFooter,
						}}
					/>
				</Stack>
			)}
		</CollapsableStack>
	);
}
